import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Col} from "reactstrap";
import {
    DataGridActionTypes,
    dataGridCellTextProps,
    DataGridColumnAlignments,
    DataGridColumnTypes,
    DataGridColumnWidthTypes,
    DataGridExoticColumnFields,
    useDataGridSavedState
} from "../../../../../../../../../../packages/data-grid";
import {FreeShippingFeesManagementCategoriesDataGridColumnNames} from "../../../../../../../../../../core/constants/table/data-grid-column-keys";
import useIsMounted from "../../../../../../../../../hooks/use-is-mounted";
import PartnerDataGrid from "../../../../../../../../../containers/data-grid";
import Api from "../../../../../../../../../../core/services/api/api";
import {FreeShippingFeesManagementViewDataGridActionNames} from "../../index";
import {OutlinedInput} from "../../../../../../../../../components/base/input/outlined_input";
import IconButton from "../../../../../../../../../components/base/button/icon_button";
import {CircularProgress, Fade, SvgIcon} from "@mui/material";
import {Edit} from "@mui/icons-material";
import classnames from "classnames";
import {formatMoney} from "../../../../../../../../../../core/services/utils/utils";
import {ReactComponent as RemoveIcon} from "../../../../../../../../../../assets/images/remove.svg";
import {OutlinedDropdown} from "../../../../../../../../../components/base/dropdown/outlined_dropdown";
import Button from "../../../../../../../../../components/base/button/button";
import useIndustry from "../../../../../../../../../hooks/use-industry";


const dataGridSaveConfig = {
    name: 'FreeShippingFeesManagementViewSettingsCategoriesExceptions',
    version: 2,
}

const FreeShippingFeesManagementViewSettingsCategoriesExceptions = ({
                                                                        syncWithDetachedCustomers,
                                                                        customerId,
                                                                        customerName,
                                                                        data: dataProp,
                                                                        refresher,
                                                                    }) => {
    const [data, setData] = useState([]);
    const {cachedIndustry, fetchedIndustry, fetchIndustry, industries, categoriesOfFetchedIndustry} = useIndustry();
    const [fetchingIndustriesForCategories, setFetchingIndustriesForCategories] = useState(false);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState('');
    const isMounted = useIsMounted();
    /** @type {React.MutableRefObject<DataGridController>}*/
    const dataGridController = useRef();

    const isStaticData = useMemo(() => !!dataProp?.length, [dataProp?.length])
    const categories = useMemo(() => categoriesOfFetchedIndustry?.filter(e => !e.parentId) ?? [], [categoriesOfFetchedIndustry])

    const dataCategoryIds = useMemo(() => data.map(e => e.originalCategoryId), [data])
    const cachedIndustryCategories = useRef({});

    /**
     * Automatically fetches the cached industry when the component is mounted
     */
    useEffect(() => {
        if (!cachedIndustry?.id)
            return;
        void fetchIndustry({industryId: cachedIndustry.id});
    }, [cachedIndustry?.id, fetchIndustry]);

    /**
     * Automatically fetches the industry if there is only one industry available and it's not already fetched or selected
     */
    useEffect(() => {
        if (industries?.length === 1) {
            const industry = industries[0];
            void fetchIndustry({industryId: industry.id});
        }
    }, [fetchIndustry, industries]);

    /**
     * Fetches the list of industries for the existing categoryIds from the server.
     * @type {(function(): Promise<void>)}
     */
    const getIndustriesOfCategories = useCallback(async () => {
        if (!dataCategoryIds?.length || !industries.length)
            return;
        let dataCategoryIdsFromApi = [];
        for (const dataCategoryId of dataCategoryIds) {
            if (!cachedIndustryCategories.current[dataCategoryId]) {
                dataCategoryIdsFromApi.push(dataCategoryId);
            }
        }
        if (dataCategoryIdsFromApi.length) {
            setFetchingIndustriesForCategories(true);
            const response = await Api.getCategoryIndustryIds({
                ids: dataCategoryIdsFromApi,
            });
            if (!isMounted())
                return;
            setFetchingIndustriesForCategories(false);
            if (response?.resultFlag) {
                cachedIndustryCategories.current = {
                    ...cachedIndustryCategories.current,
                    ...response.data.reduce((acc, e) => {
                        acc[e.id] = industries.find(i => i.id === e.industryId);
                        return acc;
                    }, {})
                }
            }
        }
    }, [cachedIndustryCategories, dataCategoryIds, isMounted, industries])

    /**
     * With each change in [dataCategoryIds]:
     * - Fetches the list of industries from the server.
     */
    useEffect(() => {
        if (fetchingIndustriesForCategories)
            return;
        getIndustriesOfCategories().then();
    }, [getIndustriesOfCategories, fetchingIndustriesForCategories]);


    const columns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                {
                    name: DataGridExoticColumnFields.selection,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    resizable: false,
                    visibilityToggleable: false,
                    pinnedToggleable: false,
                    reorderable: false,
                    sortable: false,
                    pinned: false,
                },
                ...(industries?.length > 1 ? [
                    {
                        title: "Industry",
                        name: FreeShippingFeesManagementCategoriesDataGridColumnNames.industry,
                        type: DataGridColumnTypes.string,
                        sortable: false,
                        width: {
                            size: 1,
                            type: DataGridColumnWidthTypes.flex,
                        },
                    },
                ] : []),
                {
                    title: "Category",
                    name: FreeShippingFeesManagementCategoriesDataGridColumnNames.category,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                    width: {
                        size: 1,
                        type: DataGridColumnWidthTypes.flex,
                    },
                },
                {
                    title: "Threshold",
                    name: FreeShippingFeesManagementCategoriesDataGridColumnNames.threshold,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: {
                        size: 1,
                        type: DataGridColumnWidthTypes.flex,
                    },
                },
            ],
        [])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    })

    /**
     * Searches through the entries of the free-shipping-fees management view from the server
     * @type {(function(*=, boolean=): Promise<*|[]>)|*}
     */
    const search = useCallback(async () => {
        if (isStaticData) {
            setData(dataProp);
            setLoading(false);
            return;
        }
        setLoading(true);
        const response = await Api.getFreightCategoryExceptions({
            customerOriginalId: customerId ?? null
        });
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setData(response.data ?? []);
        }
    }, [isMounted, customerId, dataProp, isStaticData]);

    /**
     * With each change in the [search] callback:
     * - searches through the entries of the free-shipping-fees management view from the server
     */
    useEffect(() => {
        search().then();
    }, [search, refresher])


    /**
     * Adds a new entity to the list of records for this free-shipping-management view
     */
    const add = useCallback(async (entry) => {
        setAdding(true);
        const forApi = {
            originalCategoryId: entry.id,
            originalCategoryTitle: entry.title,
            customerOriginalId: customerId,
            customerTitle: customerName,
            applyToAll: syncWithDetachedCustomers,
            threshold: null,
        }
        const response = await Api.saveFreightCategoryException(forApi);
        setAdding(false);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => [{...response.data, threshold: null}, ...prevState]);
            setSelectedEntity('');
        }
    }, [customerId, customerName, isMounted, syncWithDetachedCustomers])

    /**
     * Calls the api regarding removing the list of data
     * @param ids list of ids associated to entries to be removed
     * @private
     */
    const remove = useCallback(async (categoryIds) => {
        setLoading(true);
        const forApi = {
            categoryOriginalIds: categoryIds,
            customerOriginalId: customerId,
            applyToAll: syncWithDetachedCustomers,
        }
        const response = await Api.removeFreightCategoryException(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.filter(e => !categoryIds.includes(e.originalCategoryId)))
        }
        dataGridController.current?.resetSelection();
        setLoading(false);
    }, [isMounted, customerId, syncWithDetachedCustomers])

    /**
     * Updates the threshold of the entity for the fee-shipping-fees management view.
     * @type {function(*, null=): Promise<*>}
     */
    const edit = useCallback(async (entry, threshold = null) => {
        const forApi = {
            originalCategoryId: entry.originalCategoryId,
            originalCategoryTitle: entry.originalCategoryTitle,
            customerOriginalId: customerId,
            customerTitle: customerName,
            applyToAll: syncWithDetachedCustomers,
            threshold: threshold,
        }
        setData(prevState => prevState.map(e => e.id === entry.id ? {...e, editing: true} : e))
        const response = await Api.saveFreightCategoryException(forApi);
        if (!isMounted())
            return;
        setData(prevState => prevState.map(e => e.id === entry.id ? {...e, editing: false} : e))
        if (response?.resultFlag) {
            setData(prevState => {
                const newState = Array.from(prevState)
                newState.splice(
                    prevState.findIndex(e => e.id === entry.id),
                    1,
                    {...response.data, threshold: forApi.threshold}
                );
                return newState;
            })
        }
        return response?.resultFlag ?? false;
    }, [customerId, customerName, isMounted, syncWithDetachedCustomers])


    /**
     * Opens the confirmation dialog regarding this action
     * If the process was confirmed, calls the remove
     * @type {function(Array<any>)}
     */
    const getRemoveConfirmation = useCallback((itemsToBeRemoved) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    {`Are you sure that you want to remove the selected item${itemsToBeRemoved?.length > 1 ? '(s)' : ''}?`}
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && remove(itemsToBeRemoved),
        );
    }, [remove])

    /**
     * Handles the action over the selected row in the table based on the name of the action
     * @param action
     * @param selectedRows
     * @param excludedRows
     * @param allRowsSelected
     * @param pagination
     * @return {Promise<void>}
     */
    const onActionInvoked = useCallback(async (action, selectedRows, excludedRows, allRowsSelected, pagination) => {
        switch (action.name) {
            case FreeShippingFeesManagementViewDataGridActionNames.Delete: {
                if (!allRowsSelected) {
                    // not all rows are selected, therefore, the selectedRows list is not empty
                    return getRemoveConfirmation(selectedRows.map(e => e.data.originalCategoryId));
                }
                if (pagination.pageSize >= pagination.length) {
                    // all the items are within a single page, so we have the data in hand
                    return getRemoveConfirmation(data.map(e => e.originalCategoryId));
                }
                getRemoveConfirmation(data.map(e => e.originalCategoryId));
                break;
            }
            default:
                break;
        }
    }, [data, getRemoveConfirmation])

    /**
     * Commits the edited value of the provided cell belonging to the [rowKey] and [colName].
     *
     * @param prevValue
     * @param newValue
     * @param {number | string} rowKey
     * @param {number} colName
     * @return {Promise<undefined|boolean>}
     */
    const onCellEdited = useCallback(async (prevValue, newValue, rowKey, colName) => {
        switch (colName) {
            case FreeShippingFeesManagementCategoriesDataGridColumnNames.threshold:
                let threshold = Number(newValue)
                if (Number.isNaN(threshold))
                    threshold = null;
                if (threshold === 0)
                    threshold = null;
                return edit(data?.find(e => e.id === rowKey), threshold);
            default:
                return;
        }
    }, [data, edit])

    /**@type {DataGridRow[]}}*/
    const dataGridRows = useMemo(() => {
        return data.map(entry => {
            const thresholdRemovable = entry?.threshold !== null;
            return ({
                key: entry.id,
                data: entry,
                cells: {
                    [DataGridExoticColumnFields.selection]: null,
                    [FreeShippingFeesManagementCategoriesDataGridColumnNames.industry]: cachedIndustryCategories.current[entry?.originalCategoryId]?.title ?? "",
                    [FreeShippingFeesManagementCategoriesDataGridColumnNames.category]: entry?.originalCategoryTitle,
                    [FreeShippingFeesManagementCategoriesDataGridColumnNames.threshold]: {
                        value: ({openEditPopover}) =>
                            <>
                                <Fade in={thresholdRemovable}>
                                        <span>
                                        <IconButton
                                            onPressed={() => edit(entry)}
                                            disabled={entry?.editing || !thresholdRemovable}
                                            color={'paper.error'}
                                        >
                                            {
                                                entry?.editing
                                                    ? <CircularProgress size={20} color={'secondary'}/>
                                                    : <SvgIcon component={RemoveIcon} className='icon small'/>
                                            }
                                        </IconButton>
                                        </span>
                                </Fade>
                                <IconButton
                                    className={classnames({'ml-2': thresholdRemovable})}
                                    onPressed={() => openEditPopover()}
                                    disabled={entry?.editing}
                                >
                                    {
                                        entry?.editing
                                            ? <CircularProgress size={20} color={'secondary'}/>
                                            : <Edit className={"icon small"}/>
                                    }
                                </IconButton>
                                <p {...dataGridCellTextProps} className={classnames('ml-2')}>
                                    {entry.threshold === null ? 'Not Eligible at all' : formatMoney(entry.threshold)}
                                </p>
                            </>,
                        editable: true,
                        editableValue: entry.threshold,
                        EditableContentComponent: <ThresholdEditor/>,
                    },
                },
            })
        })
    }, [data, edit])

    /**
     * Memo version of data grid footer actions
     * @type {DataGridFooterAction[]}
     */
    const footerActions = useMemo(() =>
        /** @type {DataGridFooterAction[]}*/
        [
            {
                name: FreeShippingFeesManagementViewDataGridActionNames.Delete,
                title: 'Remove',
                type: DataGridActionTypes.delete,
                visibilityConstraints: {
                    minSelectedRows: 1,
                },
            },
        ], [])

    const dropdownData = useMemo(() => {
        const existingCategoryIds = data.map(e => e.originalCategoryId);
        return categories
            .filter(e => !existingCategoryIds.includes(e.id))
            .map(e => ({
                id: e.id,
                label: e.title,
                value: e,
            }))
    }, [categories, data])

    return (
        <>
            <div className={'section'}>
                <p className={'section-title'}>
                    Root Categories
                </p>
                <p className={'section-description'}>
                    Orders containing The following categories are not eligible for free-shipping. The categories with a
                    threshold, shall also be eligible for free shipping if their total in an order is more than the
                    provided threshold
                    value.
                </p>
                <div className={'section-paper'}>
                    <div className={'d-flex flex-wrap w-100'}>
                        {
                            !isStaticData &&
                            <>
                                {
                                    industries?.length > 1 &&
                                    <Col xs={12} className={'mb-3'}>
                                        <OutlinedDropdown
                                            label={'Industry'}
                                            onChange={(industry) => fetchIndustry({industryId: industry?.id})}
                                            value={fetchedIndustry}
                                            data={industries?.map(e => ({value: e, label: e.title, id: e.id})) ?? []}
                                        />
                                    </Col>
                                }
                                <Col xs={9}>
                                    <OutlinedDropdown
                                        label={'New Category'}
                                        onChange={(entity) => setSelectedEntity(entity)}
                                        value={selectedEntity}
                                        data={dropdownData}
                                        disabled={adding}
                                    />
                                </Col>
                                <Col xs={3}>
                                    <Button
                                        type={"button"}
                                        variant={"contained"}
                                        className={'py-2-5 w-100'}
                                        color={'primary'}
                                        disabled={adding || !selectedEntity}
                                        onClick={() => add(selectedEntity)}
                                    >
                                        {
                                            adding ? "Adding..." : "Add"
                                        }
                                    </Button>
                                </Col>
                            </>
                        }
                        <PartnerDataGrid
                            ref={dataGridController}
                            {...dataGridSaveConfig}
                            visibleRows={5}
                            initialLayoutState={{
                                columns: columns,
                            }}
                            hideToolbar
                            hidePagination
                            hidePageSize
                            hideSelectAll
                            columns={dataGridColumns}
                            rows={dataGridRows}
                            footerActions={footerActions}
                            onActionInvoked={onActionInvoked}
                            onCellEdited={onCellEdited}
                            hideFooter={isStaticData}
                            state={{
                                loading: {state: loading},
                                density: dataGridDensity,
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

const ThresholdEditor = ({value, setValue, setCanSubmitChanges}) => {

    /**
     * With each change in the value of the editor:
     * - Determines whether the new value is can be submitted.
     */
    useLayoutEffect(() => {
        const parsed = Number(value)
        setCanSubmitChanges(!Number.isNaN(parsed));
    }, [value])

    /**
     * Changes the value of this editor.
     *
     * - ensures the value is non-negative.
     * @param value
     */
    const onValueChanged = (value) => {
        const parsed = Number(value)
        if (!value || Number.isNaN(parsed))
            return setValue(undefined);
        setValue(Math.max(0, parsed));
    }

    return (
        <>
            <OutlinedInput
                fullWidth
                label={"Threshold"}
                size={'small'}
                value={value?.toString() ?? ''}
                onChange={onValueChanged}
                type={'number'}
                inputProps={{
                    min: 0,
                }}
            />
        </>
    );
}

export default FreeShippingFeesManagementViewSettingsCategoriesExceptions;
